import { type CSSProperties, FC, memo, ReactNode, useState } from 'react';
import { noop } from 'lodash';
import classNames from 'classnames';
import Markdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import rehypeHighlight from 'rehype-highlight';
// Import Syntax Highlighting Theme
import 'highlight.js/styles/github.css';
// Import UI Components
import { Checkbox } from '@geneui/components';
// Import Plugins & Components
import remarkAlerts from './remarkAlerts';
import MDAlert from './MDAlert';
import QA from './QA';
import MDImage from './MDImage';
import MDLink from './MDLink';
import Lightbox from './Lightbox';
// Import Styles
import styles from './MarkDownReader.module.scss';

interface Props {
    content: string;
    /**
     * Marks every block with the Markdown lines it came from (`data-sourcepos="3:1-5:12"`), so an editor can
     * scroll this preview to the part being edited.
     */
    shouldMarkSourceLines?: boolean;
    /**
     * Renders a `::children` directive, the list of an article's child articles; `articleId` is the directive's
     * optional `id`. Without it the directive renders nothing.
     */
    renderArticleChildren?: (articleId?: string) => ReactNode;
    /**
     * Prefix of the footnote ids and links (default `user-content-`). Readers shown on the same page need
     * different prefixes, or a footnote link jumps to the other reader's footnote.
     */
    footnoteIdPrefix?: string;
}

/** Block props: react-markdown sets `data-sourcepos` when `sourcePos` is on, and the overrides pass it on. */
interface BlockProps {
    children?: ReactNode;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- the HTML attribute react-markdown sets
    'data-sourcepos'?: string;
}

interface TableCellProps {
    children?: ReactNode;
    style?: CSSProperties;
}

interface LightboxState {
    src: string;
    alt?: string;
}

const MarkDownReader: FC<Props> = (props) => {
    const { content, shouldMarkSourceLines = false, footnoteIdPrefix, renderArticleChildren } = props;
    const [lightbox, setLightbox] = useState<LightboxState | null>(null);

    // react-markdown does NOT render raw HTML unless rehype-raw is added.
    // We deliberately omit it, so HTML in the source is shown as text.
    // Every element gets a dedicated class so its default look can be
    // tweaked independently from MarkDownReader.module.scss.
    const components = {
        // Headlines
        h1: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <h1 className={styles.h1} data-sourcepos={sourcePos}>
                {children}
            </h1>
        ),
        h2: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <h2 className={styles.h2} data-sourcepos={sourcePos}>
                {children}
            </h2>
        ),
        h3: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <h3 className={styles.h3} data-sourcepos={sourcePos}>
                {children}
            </h3>
        ),
        h4: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <h4 className={styles.h4} data-sourcepos={sourcePos}>
                {children}
            </h4>
        ),
        h5: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <h5 className={styles.h5} data-sourcepos={sourcePos}>
                {children}
            </h5>
        ),
        h6: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <h6 className={styles.h6} data-sourcepos={sourcePos}>
                {children}
            </h6>
        ),
        // Text styles (regular / italic / bold / bold-italic / strikethrough / inline code)
        p: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <p className={styles.text} data-sourcepos={sourcePos}>
                {children}
            </p>
        ),
        em: ({ children }: { children?: ReactNode }) => <em className={styles.italic}>{children}</em>,
        strong: ({ children }: { children?: ReactNode }) => <strong className={styles.bold}>{children}</strong>,
        del: ({ children }: { children?: ReactNode }) => <del className={styles.strikethrough}>{children}</del>,
        code: ({ inline, className, children }: { inline?: boolean; className?: string; children?: ReactNode }) =>
            inline ? (
                <code className={styles.inlineCode}>{children}</code>
            ) : (
                // Block code keeps the class rehype-highlight added so
                // syntax highlighting still works.
                <code className={className}>{children}</code>
            ),
        // Horizontal divider
        hr: ({ 'data-sourcepos': sourcePos }: BlockProps) => (
            <hr className={styles.divider} data-sourcepos={sourcePos} />
        ),
        // Lists
        ul: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <ul className={styles.bulletList} data-sourcepos={sourcePos}>
                {children}
            </ul>
        ),
        ol: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <ol className={styles.orderedList} data-sourcepos={sourcePos}>
                {children}
            </ol>
        ),
        // remark-gfm marks task list items ("- [x] text") with the task-list-item class
        // The id is a footnote's: its reference links jump to it
        li: ({
            children,
            id,
            className,
            'data-sourcepos': sourcePos,
        }: BlockProps & { id?: string; className?: string }) => (
            <li
                id={id}
                className={classNames(styles.listItem, {
                    [styles.taskListItem]: className?.includes('task-list-item'),
                })}
                data-sourcepos={sourcePos}
            >
                {children}
            </li>
        ),
        // The global gene-ui reset hides native inputs (appearance: none), so task list checkboxes are drawn
        // with the gene-ui Checkbox; react-markdown emits no other inputs because raw HTML is not rendered.
        // Not `readOnly`: gene-ui draws read-only checkboxes as bare check / cross icons without the box.
        // The value is controlled with a no-op onChange, and the class blocks pointer interaction.
        input: ({ type, checked }: { type?: string; checked?: boolean }) =>
            type === 'checkbox' ? (
                <Checkbox className={styles.taskCheckbox} checked={!!checked} tabIndex={-1} onChange={noop} />
            ) : null,
        // Hyperlink (see MDLink — internal routes never reload the SPA)
        a: MDLink,
        // Table
        table: ({ children, 'data-sourcepos': sourcePos }: BlockProps) => (
            <div className={styles.tableWrapper} data-sourcepos={sourcePos}>
                <table className={styles.table}>{children}</table>
            </div>
        ),
        thead: ({ children }: { children?: ReactNode }) => <thead className={styles.tableHead}>{children}</thead>,
        tbody: ({ children }: { children?: ReactNode }) => <tbody className={styles.tableBody}>{children}</tbody>,
        // react-markdown turns the column alignment (":---:", "---:") into style.textAlign
        th: ({ children, style }: TableCellProps) => (
            <th className={styles.tableHeadCell} style={style}>
                {children}
            </th>
        ),
        td: ({ children, style }: TableCellProps) => (
            <td className={styles.tableCell} style={style}>
                {children}
            </td>
        ),
        // Blocks & accordion (see remarkAlerts)
        alert: MDAlert,
        qa: QA,
        articleChildren: ({ articleId, 'data-sourcepos': sourcePos }: BlockProps & { articleId?: string }) =>
            renderArticleChildren ? (
                <div className={styles.articleChildren} data-sourcepos={sourcePos}>
                    {renderArticleChildren(articleId)}
                </div>
            ) : null,
        img: ({ src, alt, title }: { src?: string; alt?: string; title?: string }) => (
            <MDImage
                src={src}
                alt={alt}
                title={title}
                onPreview={(previewSrc) => setLightbox({ src: previewSrc, alt })}
            />
        ),
    } as unknown as Components;

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <Markdown
                    remarkPlugins={[remarkGfm, remarkDirective, remarkAlerts]}
                    rehypePlugins={[rehypeHighlight]}
                    remarkRehypeOptions={{ clobberPrefix: footnoteIdPrefix }}
                    components={components}
                    sourcePos={shouldMarkSourceLines}
                >
                    {content}
                </Markdown>
            </div>
            {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
        </div>
    );
};

export default memo(MarkDownReader);
